
import React, { useState, useEffect, useCallback } from 'react';
import { Save, Loader2 } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { ProjectCombobox } from '@/components/ui/ProjectCombobox';
import { Project } from '@/hooks/useProjects';

interface ProjectSelectorProps {
  projects: Project[];
  selectedProject: string;
  description: string;
  onProjectChange: (projectId: string) => void;
  onDescriptionChange: (description: string) => void;
  disabled: boolean;
  isTracking?: boolean;
  onDescriptionUpdate?: (description: string) => Promise<void>;
}

const ProjectSelector: React.FC<ProjectSelectorProps> = ({
  projects,
  selectedProject,
  description,
  onProjectChange,
  onDescriptionChange,
  disabled,
  isTracking = false,
  onDescriptionUpdate,
}) => {
  const [localDescription, setLocalDescription] = useState(description);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Sync avec la description externe
  useEffect(() => {
    setLocalDescription(description);
    setHasChanges(false);
  }, [description]);

  const handleDescriptionChange = useCallback((value: string) => {
    setLocalDescription(value);
    setHasChanges(value !== description);
    if (!isTracking) {
      onDescriptionChange(value);
    }
  }, [description, isTracking, onDescriptionChange]);

  const handleSaveDescription = useCallback(async () => {
    if (!onDescriptionUpdate || !hasChanges) return;

    setIsSaving(true);
    try {
      await onDescriptionUpdate(localDescription);
      setHasChanges(false);
    } finally {
      setIsSaving(false);
    }
  }, [localDescription, hasChanges, onDescriptionUpdate]);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="project">Projet *</Label>
        <ProjectCombobox
          projects={projects}
          value={selectedProject}
          onValueChange={onProjectChange}
          disabled={disabled}
          placeholder="Choisir un projet"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description (optionnel)</Label>
        <div className="flex gap-2">
          <Textarea
            id="description"
            value={localDescription}
            onChange={(e) => handleDescriptionChange(e.target.value)}
            placeholder="Décrivez votre tâche..."
            className="flex-1 min-h-[60px] resize-none"
            rows={2}
          />
          {isTracking && hasChanges && (
            <Button
              size="icon"
              onClick={handleSaveDescription}
              disabled={isSaving}
              title="Enregistrer la description"
              className="self-start"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectSelector;
